import {
    ApolloClient, ApolloLink, ApolloProvider, InMemoryCache, HttpLink, from,
} from '@apollo/client';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { getBearerAccessToken, getGraphQLApiUrl } from 'additiv-services/setup';
import { adaptQueryParams } from './utils';
import { GraphQLOperations } from '../../constants/constants';

const httpLink = new HttpLink({ uri: `${getGraphQLApiUrl()}` });

const authMiddlewareInit = () => new ApolloLink((operation, forward) => {
    const jwt = getBearerAccessToken();
    let operationRoute;

    if (operation && operation.operationName) {
        operationRoute = GraphQLOperations[operation.operationName]?.Route;

        if (!operationRoute) {
            console.log(`⚠️ GraphQL Operation Route Mapping not available for ${operation.operationName}!!!`);
        }
    }

    if (jwt) {
        const { queryParams = {} } = operation.getContext();

        operation.setContext(({ headers = {} }) => ({
            headers: { ...headers, Authorization: jwt },
            uri: `${getGraphQLApiUrl()}${operationRoute ?? ''}?${operation.operationName}${adaptQueryParams(queryParams)}`,
        }));
    }

    return forward(operation);
});

const languageMiddlewareInit = (language) => new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
        headers: { ...headers, 'accept-language': language },
    }));

    return forward(operation);
});

const initClient = (language) => {
    const authMiddleware = authMiddlewareInit();
    const languageMiddleware = languageMiddlewareInit(language);

    return new ApolloClient({
        defaultOptions: {
            watchQuery: {
                fetchPolicy: 'no-cache',
                errorPolicy: 'ignore',
            },
            query: {
                fetchPolicy: 'no-cache',
                errorPolicy: 'all',
            },
        },
        cache: new InMemoryCache(),
        link: from([
            authMiddleware,
            languageMiddleware,
            httpLink,
        ]),
    });
};

export const ApolloClientProvider = (props) => {
    const { children } = props;
    const { i18n: { language } } = useTranslation();
    const client = useMemo(() => (initClient(language)), [language]);

    return (
        <ApolloProvider client={client}>
            {children}
        </ApolloProvider>
    );
};
